import React, { PropsWithChildren } from 'react';
import { useQuerySet } from '~/contexts/QuerysetContext';
import { ResultProduct } from '~/core/results/types/results.type';
import { useShownAttributes } from '~/hooks/useShownAttributes';
import { CustomProductColorsConfiguration } from './productResultContainer';

export type Style = {
    [className: string]: string;
};

export interface ProductSingleResultContext {
    result: Omit<Partial<ResultProduct>, 'title' | 'url'> & Pick<ResultProduct, 'title' | 'url'>;
    fullResult: ResultProduct;
    linkElementRef: React.RefObject<HTMLDivElement>;
    customColorsConfiguration: CustomProductColorsConfiguration;
    style: Style;
}

export const ProductContext = React.createContext<ProductSingleResultContext | undefined>(undefined);

export const ProductContextProvider: React.FC<
    PropsWithChildren<{
        result: ResultProduct;
        linkElementRef: React.RefObject<HTMLDivElement>;
        customColorsConfiguration: CustomProductColorsConfiguration;
        style: Style;
    }>
> = ({ result, linkElementRef, customColorsConfiguration, children, style }) => {
    const { state: querySetState } = useQuerySet();
    const { stripNotRenderedAttributes } = useShownAttributes({ querySetState });
    const strippedResult = stripNotRenderedAttributes(result);

    return (
        <ProductContext.Provider
            value={{ result: strippedResult, fullResult: result, linkElementRef, customColorsConfiguration, style }}
        >
            {children}
        </ProductContext.Provider>
    );
};

export const useProductContext = () => {
    const context = React.useContext(ProductContext);
    if (context === undefined) {
        throw new Error('useProductContext must be used within a ProductContext');
    }

    return context;
};
